import { Box, CircularProgress } from "@mui/material";
import { useEffect, useRef, useState } from "react";

interface DocxDisplayProps {
  url: string;
}

export function DocxDisplay(props: DocxDisplayProps) {
  const { url } = props;
  const [documentLoadCheckTimes, setDocumentLoadCheckTimes] = useState(0);
  const [documentLoadRetrying, setDocumentLoadRetrying] = useState(false);
  const nextGoogleViewerRun = useRef<NodeJS.Timeout | undefined>();

  /**
   * Why this hack:
   * - The iframe loads Google Docs Viewer (GDV), not the DOCX itself.
   * - GDV can appear “loaded” but show a blank page (cache/BFCache, expired presign, etc.).
   * - Heuristic: if iframe body is empty, we force a remount by changing the `key`.
   * - We poll briefly and cap retries to avoid loops.
   */

  // eslint-disable-next-line unicorn/consistent-function-scoping
  async function queryDocumentViewer(): Promise<Document | null | undefined> {
    try {
      const iframe = document.querySelector<HTMLIFrameElement>("#doc-viewer");
      return iframe?.contentDocument ?? iframe?.contentWindow?.document;
    } catch {
      return null;
    }
  }

  async function checkGoogleViewerLoad(documentLoadCheckTimes = 0) {
    const iframeDocument = await queryDocumentViewer();

    if (!iframeDocument) {
      setDocumentLoadRetrying(false);
      return;
    }

    setDocumentLoadCheckTimes((previousState) => previousState + 1);
    if (iframeDocument?.body.children.length === 0 && documentLoadCheckTimes < 10) {
      setDocumentLoadRetrying(true);
      nextGoogleViewerRun.current = setTimeout(() => {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        checkGoogleViewerLoad(documentLoadCheckTimes + 1);
      }, 3000);
    } else {
      setDocumentLoadRetrying(false);
    }
  }

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    checkGoogleViewerLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
      }}
    >
      {/* eslint-disable-next-line react/iframe-missing-sandbox */}
      <iframe
        key={`doc-viewer-${documentLoadCheckTimes}`}
        title="Doc Viewer"
        id="doc-viewer"
        src={`https://docs.google.com/viewer?embedded=true&url=${encodeURIComponent(url)}`}
        style={{
          width: "100%",
          height: "80vh",
          border: 0,
        }}
        allow="fullscreen"
        onError={(error) => {
          // eslint-disable-next-line no-console
          console.error("Unable to view the document", { cause: error, url });
        }}
        onLoad={() => {
          if (nextGoogleViewerRun.current) {
            clearTimeout(nextGoogleViewerRun.current);
          }

          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          checkGoogleViewerLoad();
        }}
      />
      {documentLoadRetrying ? <CircularProgress sx={{ position: "absolute" }} /> : undefined}
    </Box>
  );
}
